import { Component, inject, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Event } from './events.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { ViewChild, ElementRef } from '@angular/core';
import { CloudinaryUploadService } from '../upload-ui/cloudinary-upload.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

interface FormDialogData {
  mode: 'create' | 'edit';
  event?: Event;
  organizationId?: string;
}

@Component({
  selector: 'app-event-form-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
  ],
  template: `
    <div class="event-form-dialog">
      <div class="dialog-header">
        <div class="header-content">
          <mat-icon class="header-icon">{{ data.mode === 'create' ? 'add_circle' : 'edit' }}</mat-icon>
          <h2 class="header-title">{{ data.mode === 'create' ? 'Create New Event' : 'Edit Event' }}</h2>
        </div>
        <button mat-icon-button (click)="close()" class="close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content>
        <form [formGroup]="eventForm" class="event-form">
          <!-- Timezone Info Banner -->


          <!-- Title -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Event Title</mat-label>
            <input matInput formControlName="title" placeholder="Enter event title">
            <mat-error *ngIf="eventForm.get('title')?.hasError('required')">
              Title is required
            </mat-error>
          </mat-form-field>

          <!-- Description -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Description</mat-label>
            <textarea
              matInput
              formControlName="description"
              placeholder="Enter event description"
              rows="4">
            </textarea>
          </mat-form-field>

          <!-- Date and Time Row -->
          <div class="form-row">
            <div class="date-time-group">
              <mat-form-field appearance="outline" class="date-field">
                <mat-label>Start Date</mat-label>
                <input
                  matInput
                  [matDatepicker]="startPicker"
                  formControlName="startDate"
                  placeholder="Select start date">
                <mat-datepicker-toggle matIconSuffix [for]="startPicker"></mat-datepicker-toggle>
                <mat-datepicker #startPicker></mat-datepicker>
                <mat-error *ngIf="eventForm.get('startDate')?.hasError('required')">
                  Start date is required
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="time-field">
                <mat-label>Start Time</mat-label>
                <mat-select formControlName="startTime">
                  <mat-option *ngFor="let time of timeOptions" [value]="time">
                    {{ time }}
                  </mat-option>
                </mat-select>
                <mat-icon matIconSuffix>schedule</mat-icon>
                <mat-error *ngIf="eventForm.get('startTime')?.hasError('required')">
                  Start time is required
                </mat-error>
              </mat-form-field>
            </div>

            <div class="date-time-group">
              <mat-form-field appearance="outline" class="date-field">
                <mat-label>End Date</mat-label>
                <input
                  matInput
                  [matDatepicker]="endPicker"
                  formControlName="endDate"
                  placeholder="Select end date">
                <mat-datepicker-toggle matIconSuffix [for]="endPicker"></mat-datepicker-toggle>
                <mat-datepicker #endPicker></mat-datepicker>
                <mat-error *ngIf="eventForm.get('endDate')?.hasError('required')">
                  End date is required
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="time-field">
                <mat-label>End Time</mat-label>
                <mat-select formControlName="endTime">
                  <mat-option *ngFor="let time of timeOptions" [value]="time">
                    {{ time }}
                  </mat-option>
                </mat-select>
                <mat-icon matIconSuffix>schedule</mat-icon>
                <mat-error *ngIf="eventForm.get('endTime')?.hasError('required')">
                  End time is required
                </mat-error>
              </mat-form-field>
            </div>
          </div>

          <!-- Type and Platform Row -->
          <div class="form-row">
            <mat-form-field appearance="outline">
              <mat-label>Event Type</mat-label>
              <mat-select formControlName="type">
                <mat-option value="training">Training</mat-option>
                <mat-option value="webinar">Webinar</mat-option>
                <mat-option value="workshop">Workshop</mat-option>
                <mat-option value="conference">Conference</mat-option>
                <mat-option value="seminar">Seminar</mat-option>
                <mat-option value="meetup">Meetup</mat-option>
                <mat-option value="networking">Networking Event</mat-option>
              </mat-select>
              <mat-error *ngIf="eventForm.get('type')?.hasError('required')">
                Event type is required
              </mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Platform</mat-label>
              <mat-select formControlName="platform">
                <mat-option value="Virtual">Virtual</mat-option>
                <mat-option value="Physical">Physical</mat-option>
                <mat-option value="Hybrid">Hybrid</mat-option>
                <mat-option value="Zoom">Zoom</mat-option>
                <mat-option value="Google Meet">Google Meet</mat-option>
                <mat-option value="Microsoft Teams">Microsoft Teams</mat-option>
                <mat-option value="Webex">Webex</mat-option>
              </mat-select>
              <mat-error *ngIf="eventForm.get('platform')?.hasError('required')">
                Platform is required
              </mat-error>
            </mat-form-field>
          </div>

          <!-- Location -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Location (if Physical/Hybrid)</mat-label>
            <input matInput formControlName="location" placeholder="Enter event location">
          </mat-form-field>

          <!-- Participants (Roles) -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Target Participants (Roles)</mat-label>
            <mat-select formControlName="participants" multiple>
              <mat-option value="admin">Admin</mat-option>
              <mat-option value="doctor">Doctor</mat-option>
              <mat-option value="nurse">Nurse</mat-option>
              <mat-option value="patient">Patient</mat-option>
              <mat-option value="lab">Lab Technician</mat-option>
              <mat-option value="pharmacy">Pharmacy Staff</mat-option>
              <mat-option value="receptionist">Receptionist</mat-option>
                    <mat-option value="serviceproviders">Service Providers</mat-option>
            </mat-select>
            <mat-hint>Leave empty for public event</mat-hint>
          </mat-form-field>

          <!-- Max Attendees and Type Row -->
          <div class="form-row">
            <mat-form-field appearance="outline">
              <mat-label>Max Attendees</mat-label>
              <input matInput type="number" formControlName="maxAttendees" placeholder="Leave empty for unlimited">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Tags</mat-label>
              <input matInput formControlName="tags" placeholder="Comma-separated tags">
              <mat-hint>e.g., healthcare, training, certification</mat-hint>
            </mat-form-field>
          </div>

          <!-- Meeting URL -->
        <!-- Meeting URL -->
<mat-form-field appearance="outline" class="full-width">
  <mat-label>{{ isZoomPlatform ? 'Zoom Meeting (auto-generated)' : 'Registration/Meeting URL' }}</mat-label>
  <input matInput formControlName="meetingUrl"
         [placeholder]="isZoomPlatform ? 'Will be created automatically when you save' : 'https://zoom.us/j/...'">
  <mat-icon matSuffix *ngIf="isZoomPlatform">bolt</mat-icon>
  <mat-hint *ngIf="isZoomPlatform">
    Zoom will create this meeting automatically. The host link will be emailed to you separately.
  </mat-hint>
  <mat-error *ngIf="eventForm.get('meetingUrl')?.hasError('pattern')">
    Please enter a valid URL
  </mat-error>
</mat-form-field>

          <!-- Image URL -->
         <div class="image-upload-field">
  <label class="image-upload-label">Event Image</label>

  <input
    #imageFileInput
    type="file"
    accept="image/*"
    hidden
    (change)="onImageFileSelected($event)">

  <!-- No image yet: show upload button -->
  <div class="image-upload-box" *ngIf="!eventForm.get('image')?.value">
    <button mat-stroked-button type="button" (click)="triggerImageUpload()" [disabled]="isUploadingImage">
      <mat-icon>upload</mat-icon>
      {{ isUploadingImage ? 'Uploading...' : 'Upload Image' }}
    </button>
    <mat-spinner *ngIf="isUploadingImage" diameter="20"></mat-spinner>
  </div>

  <!-- Image already set: show preview + replace/remove -->
  <div class="image-preview" *ngIf="eventForm.get('image')?.value">
    <img [src]="eventForm.get('image')?.value" alt="Event image preview">
    <button mat-icon-button type="button" class="remove-image-btn"
            (click)="removeImage()" [disabled]="isUploadingImage">
      <mat-icon>close</mat-icon>
    </button>
    <button mat-stroked-button type="button" (click)="triggerImageUpload()" [disabled]="isUploadingImage">
      {{ isUploadingImage ? 'Uploading...' : 'Replace' }}
    </button>
  </div>

  <div class="upload-error" *ngIf="imageUploadError">{{ imageUploadError }}</div>
</div>
        </form>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button mat-button (click)="close()">Cancel</button>
        <button
          mat-raised-button
          color="primary"
          (click)="onSubmit()"
          [disabled]="eventForm.invalid">
          {{ data.mode === 'create' ? 'Create Event' : 'Update Event' }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .event-form-dialog {
      display: flex;
      flex-direction: column;
      width: 100%;
      min-width: 0;
    }
.image-upload-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.image-preview {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;

  img {
    max-width: 200px;
    max-height: 150px;
    border-radius: 0.5rem;
    object-fit: cover;
    display: block;
  }
  .remove-image-btn {
    position: absolute;
    top: -10px;
    right: -10px;
    background: white;
    box-shadow: 0 1px 4px rgba(0,0,0,.2);
  }
}
.upload-error {
  color: #dc2626;
  font-size: 0.8rem;
}
.image-upload-label {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;
}
.image-upload-box {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
    .dialog-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: linear-gradient(135deg, #007664 0%, #005a4a 100%);
      color: white;
      padding: 1.5rem 2rem;
      margin: -24px -24px 0 -24px;
      border-radius: 4px 4px 0 0;

      .header-content {
        display: flex;
        align-items: center;
        gap: 1rem;

        .header-icon {
          font-size: 2.5rem;
          width: 2.5rem;
          height: 2.5rem;
          color: white;
        }

        .header-title {
          margin: 0;
          font-size: 2rem;
          font-weight: 700;
          letter-spacing: -0.5px;
          line-height: 1.2;
        }
      }

      .close-btn {
        color: white;
        flex-shrink: 0;

        &:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
      }
    }

    mat-dialog-content {
      padding: 2rem;
      max-height: 65vh;
      overflow-y: auto;
      flex: 1;
    }

    .timezone-banner {
      background-color: #e0f2fe;
      border: 1px solid #7dd3fc;
      border-radius: 0.5rem;
      padding: 1rem;
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
      color: #0c4a6e;

      mat-icon {
        color: #0284c7;
        font-size: 1.25rem;
        width: 1.25rem;
        height: 1.25rem;
        flex-shrink: 0;
        margin-top: 2px;
      }

      span {
        font-size: 0.875rem;
        line-height: 1.5;
        font-weight: 500;
      }
    }

    .event-form {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }

    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.5rem;

      @media (max-width: 700px) {
        grid-template-columns: 1fr;
      }
    }

    .date-time-group {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 1rem;
      align-items: flex-start;

      @media (max-width: 700px) {
        grid-template-columns: 1fr;
      }

      .date-field {
        width: 100%;
      }

      .time-field {
        width: 100%;
      }
    }

    mat-form-field {
      width: 100%;

      &.full-width {
        width: 100%;
      }
    }

    mat-dialog-actions {
      padding: 1.5rem 2rem;
      border-top: 1px solid #e5e7eb;
      gap: 1rem;
      background-color: #f9fafb;

      button {
        min-width: 120px;
      }
    }

    /* Custom scrollbar styling */
    mat-dialog-content::-webkit-scrollbar {
      width: 6px;
    }

    mat-dialog-content::-webkit-scrollbar-track {
      background: #f1f5f9;
    }

    mat-dialog-content::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 3px;

      &:hover {
        background: #94a3b8;
      }
    }
  `]
})
export class EventFormDialogComponent implements OnInit {
  eventForm!: FormGroup;
  timeOptions: string[] = [];
private cloudinaryUploadService = inject(CloudinaryUploadService);

  @ViewChild('imageFileInput') imageFileInput!: ElementRef<HTMLInputElement>;

  isUploadingImage = false;
  imageUploadError = '';
  get isZoomPlatform(): boolean {
  return this.eventForm?.get('platform')?.value === 'Zoom';
}
  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<EventFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FormDialogData
  ) {
    this.generateTimeOptions();
  }

  ngOnInit(): void {
    this.initForm();

    this.eventForm.get('platform')?.valueChanges.subscribe((platform: string) => {
    const meetingUrlControl = this.eventForm.get('meetingUrl');
    if (!meetingUrlControl) return;

    if (platform === 'Zoom') {
      meetingUrlControl.disable({ emitEvent: false });
    } else {
      meetingUrlControl.enable({ emitEvent: false });
    }
  });

  // Apply initial disabled state on load (e.g. editing an existing Zoom event)
  if (this.isZoomPlatform) {
    this.eventForm.get('meetingUrl')?.disable({ emitEvent: false });
  }
  }

  generateTimeOptions(): void {
    this.timeOptions = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const h = String(hour).padStart(2, '0');
        const m = String(minute).padStart(2, '0');
        this.timeOptions.push(`${h}:${m}`);
      }
    }
  }

  /**
   * Get user's timezone abbreviation for display
   */
  getUserTimezone(): string {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZoneName: 'short'
      });
      const parts = formatter.formatToParts(new Date());
      const timeZonePart = parts.find(part => part.type === 'timeZoneName');
      return timeZonePart?.value || 'Local Time';
    } catch (error) {
      console.error('Error getting timezone:', error);
      return 'Local Time';
    }
  }

  /**
   * CRITICAL FIX: Convert SQL Server UTC datetime to local Date object
   * SQL Server format: "2026-01-28 16:00:00.0000000" (UTC)
   * Need to convert to: Date object in user's local timezone
   */
  convertUTCToLocalDate(utcDateTimeString: string): Date {
    if (!utcDateTimeString) {
      return new Date();
    }

    // SQL Server datetime format: "2026-01-28 16:00:00.0000000"
    // Convert to ISO format with Z suffix to indicate UTC
    // "2026-01-28 16:00:00" → "2026-01-28T16:00:00Z"
    const isoString = utcDateTimeString.replace(' ', 'T').split('.')[0] + 'Z';

    // Create Date object - browser automatically converts UTC to local timezone
    // Database has: 16:00 UTC
    // User in Nigeria sees: 17:00 WAT (UTC+1)
    const localDate = new Date(isoString);

    console.log('UTC from DB:', utcDateTimeString);
    console.log('ISO String:', isoString);
    console.log('Local Date:', localDate.toString());

    return localDate;
  }

  initForm(): void {
    const urlPattern = /^(https?:\/\/.+)?$/;

    // When editing, convert UTC times from backend to local time for display
    let startDate = null;
    let startTime = '';
    let endDate = null;
    let endTime = '';

    if (this.data.event?.start) {
      // CRITICAL: Properly convert UTC to local time
      // Database: "2026-01-28 16:00:00" (UTC)
      // Form shows: 17:00 for Nigeria user (UTC+1)
      const startDateTime = this.convertUTCToLocalDate(this.data.event.start);
      startDate = startDateTime;
      startTime = this.formatTimeForInput(startDateTime);
    }

    if (this.data.event?.end) {
      const endDateTime = this.convertUTCToLocalDate(this.data.event.end);
      endDate = endDateTime;
      endTime = this.formatTimeForInput(endDateTime);
    }

    this.eventForm = this.fb.group({
      title: [this.data.event?.title || '', Validators.required],
      description: [this.data.event?.description || ''],
      startDate: [startDate, Validators.required],
      startTime: [startTime, Validators.required],
      endDate: [endDate, Validators.required],
      endTime: [endTime, Validators.required],
      type: [this.data.event?.type || '', Validators.required],
      platform: [this.data.event?.platform || '', Validators.required],
      location: [this.data.event?.location || ''],
      participants: [this.data.event?.participants || []],
      maxAttendees: [this.data.event?.maxAttendees || ''],
      tags: [this.data.event?.tags ? (this.data.event.tags as string[]).join(', ') : ''],
      meetingUrl: [this.data.event?.meetingUrl || '', Validators.pattern(urlPattern)],
      image: [this.data.event?.image || '']
    });
  }

  /**
   * Format time from Date object to HH:mm string
   */
  formatTimeForInput(date: Date): string {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  triggerImageUpload(): void {
    this.imageFileInput.nativeElement.value = '';
    this.imageFileInput.nativeElement.click();
  }

 onImageFileSelected(event: globalThis.Event): void {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  if (!file.type.startsWith('image/')) {
    this.imageUploadError = 'Please select an image file.';
    return;
  }

  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    this.imageUploadError = 'Image must be smaller than 5MB.';
    return;
  }

  this.imageUploadError = '';
  this.isUploadingImage = true;

  this.cloudinaryUploadService.uploadFile(file).subscribe({
    next: (result) => {
      this.eventForm.patchValue({ image: result.secure_url });
      this.isUploadingImage = false;
    },
    error: (err) => {
      console.error('Image upload failed:', err);
      this.imageUploadError = 'Failed to upload image. Please try again.';
      this.isUploadingImage = false;
    }
  });
}

  removeImage(): void {
    this.eventForm.patchValue({ image: '' });
  }

  /**
   * CRITICAL: This method handles timezone conversion properly
   * User enters times in their LOCAL timezone
   * We convert to UTC before sending to backend
   * Backend stores as UTC
   * When displaying, we convert UTC back to local time
   */
 onSubmit(): void {
  if (this.eventForm.valid) {
    const formValue = this.eventForm.getRawValue(); // use getRawValue to include disabled fields too, then explicitly null them out below

    // Create dates in LOCAL timezone first
    const startDate = new Date(formValue.startDate);
    const [startHours, startMinutes] = formValue.startTime.split(':');
    startDate.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);

    const endDate = new Date(formValue.endDate);
    const [endHours, endMinutes] = formValue.endTime.split(':');
    endDate.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);

    const eventData = {
      title: formValue.title,
      description: formValue.description,
      type: formValue.type,
      platform: formValue.platform,
      location: formValue.location,
      participants: formValue.participants,
      maxAttendees: formValue.maxAttendees || null,
      tags: formValue.tags
        ? formValue.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag)
        : [],
      // ✅ For Zoom, let the backend own meetingUrl entirely — don't send admin-typed/stale value
      meetingUrl: this.isZoomPlatform ? null : (formValue.meetingUrl || null),
      image: formValue.image || null,
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      organizationId: this.data.organizationId
    };

    console.log('Submitting event data:', {
      userTimezone: this.getUserTimezone(),
      localStart: startDate.toString(),
      utcStart: eventData.start,
      localEnd: endDate.toString(),
      utcEnd: eventData.end
    });

    this.dialogRef.close({ success: true, data: eventData });
  }
}

  close(): void {
    this.dialogRef.close({ success: false });
  }
}
